import 'react-phone-input-2/lib/style.css';

import { Input } from '@ifood/pomodoro-components';
import { pick } from '@styled-system/props';
import { useField, useFormikContext } from 'formik';
import { CountryCode } from 'libphonenumber-js';
import React, { useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import pt from 'react-phone-input-2/lang/pt.json';
import { LayoutProps, SpaceProps } from 'styled-system';

import { FieldName } from 'types/form';
import { PropsOf } from 'types/jsx';

import FieldError from '../FieldError';
import FieldLabel from '../FieldLabel';
import { StyleWrapper } from './style';

export type PhoneValue = {
  rawPhone: string;
  maskedPhone: string;
  country?: CountryCode;
};

export type PhoneProps = {
  name: FieldName<PhoneValue>;
} & React.InputHTMLAttributes<HTMLInputElement> &
  Pick<PropsOf<typeof Input>, 'label'> &
  SpaceProps &
  LayoutProps;

const STYLE = {
  containerClass: 'containerClass',
  inputClass: 'inputClass',
  buttonClass: 'buttonClass',
  searchClass: 'searchClass',
};

// See https://github.com/bl00mber/react-phone-input-2/blob/master/src/rawCountries.js
// for the list of country-codes and other mask formats
const CUSTOM_MASKS = {
  br: '(..) ....-.....',
  co: '...-....',
};

const VALID_COUNTRIES = ['br', 'co'];

const DEFAULT_COUNTRY = 'br';

export const Phone = ({ name, label, ...props }: PhoneProps): JSX.Element => {
  const [, { error, touched, initialValue }, { setValue }] = useField(name);
  const [internalValue, setInternalValue] = useState(initialValue?.rawPhone);

  const formik = useFormikContext();

  const styledSystemProps = pick(props);

  return (
    <StyleWrapper disabled={props.disabled} hasError={Boolean(error && touched)} {...styledSystemProps}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <PhoneInput
        {...STYLE}
        masks={CUSTOM_MASKS}
        country={DEFAULT_COUNTRY}
        localization={pt}
        onlyCountries={VALID_COUNTRIES}
        disabled={props.disabled}
        inputProps={{ name, ...props }}
        value={internalValue}
        enableLongNumbers={false}
        onBlur={formik.handleBlur}
        onChange={(newValue, data, event, formattedValue): void => {
          setInternalValue(newValue);
          const code: string = (data as Record<string, string>).countryCode;

          setValue({
            rawPhone: newValue,
            maskedPhone: formattedValue,
            country: code ? (code.toUpperCase() as CountryCode) : undefined,
          });
        }}
      />
      {touched && error && <FieldError>{error}</FieldError>}
    </StyleWrapper>
  );
};
